import "@babylonjs/loaders/glTF";
import { AssetContainer,Scene,SceneLoader,TransformNode,Vector3 } from "@babylonjs/core";

export interface ModelOptions {
  scale?: number;
  rotation?: Vector3;
  offset?: Vector3;
  normalizeBy?: "maximum" | "height";
  playAnimations?: boolean;
}

export async function resolveAssetWithFallback<T>(loader: () => Promise<T>, fallback: () => T): Promise<{ value: T; usedFallback: boolean }> {
  try { return { value: await loader(), usedFallback: false }; }
  catch { return { value: fallback(), usedFallback: true }; }
}

export class GameAssetManager {
  private readonly containers = new Map<string, Promise<AssetContainer | null>>();

  constructor(private readonly scene: Scene) {}

  private load(path: string): Promise<AssetContainer | null> {
    const cached = this.containers.get(path);
    if (cached) return cached;
    const slash = path.lastIndexOf("/");
    const rootUrl = path.slice(0, slash + 1);
    const fileName = path.slice(slash + 1);
    const promise = SceneLoader.LoadAssetContainerAsync(rootUrl, fileName, this.scene)
      .then((container) => {
        console.info(`[Aset] Dimuatkan: ${path}`);
        return container;
      })
      .catch((error: unknown) => {
        console.warn(`[Aset] Gagal memuat ${path}; menggunakan model prosedural.`, error);
        return null;
      });
    this.containers.set(path, promise);
    return promise;
  }

  async instantiate(path: string, name: string, options: ModelOptions = {}): Promise<TransformNode | null> {
    const container = await this.load(path);
    if (!container || this.scene.isDisposed) return null;
    const instance = container.instantiateModelsToScene((sourceName) => `${name}-${sourceName}`, false);
    const root = new TransformNode(name, this.scene);
    for (const node of instance.rootNodes) node.parent = root;
    root.rotation = options.rotation?.clone() ?? Vector3.Zero();
    root.position = options.offset?.clone() ?? Vector3.Zero();

    const meshes = root.getChildMeshes(false);
    if (meshes.length > 0) {
      let min = new Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
      let max = new Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
      for (const mesh of meshes) {
        mesh.computeWorldMatrix(true);
        const bounds = mesh.getBoundingInfo().boundingBox;
        min = Vector3.Minimize(min, bounds.minimumWorld);
        max = Vector3.Maximize(max, bounds.maximumWorld);
        mesh.isPickable = false;
      }
      const extent = options.normalizeBy === "height" ? Math.max(max.y - min.y, 0.001) : Math.max(max.x - min.x, max.y - min.y, max.z - min.z, 0.001);
      const normalizedScale = (options.scale ?? 1) / extent;
      root.scaling.setAll(normalizedScale);
      const authoredOrigin = new Vector3((min.x + max.x) * 0.5, min.y, (min.z + max.z) * 0.5);
      root.position.subtractInPlace(authoredOrigin.scale(normalizedScale));
      root.metadata = { modelBounds: { width: (max.x - min.x) * normalizedScale, height: (max.y - min.y) * normalizedScale, depth: (max.z - min.z) * normalizedScale } };
    }
    const animationGroups = instance.animationGroups ?? [];
    if (options.playAnimations) for (const group of animationGroups) group.start(true);
    root.metadata = {
      ...root.metadata,
      animationCount: animationGroups.length,
      animationNames: animationGroups.map((group) => group.name),
      animationGroups,
      skeletonBoneCount: (instance.skeletons ?? []).reduce((count, skeleton) => count + skeleton.bones.length, 0),
    };
    root.onDisposeObservable.addOnce(() => { for (const group of animationGroups) { group.stop(); group.dispose(); } });
    return root;
  }

  async preload(paths: readonly string[]): Promise<void> {
    await Promise.all(paths.map((path) => this.load(path)));
  }

  dispose(): void {
    for (const promise of this.containers.values()) void promise.then((container) => container?.dispose());
    this.containers.clear();
  }
}
